import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';

interface UseApiOptions<T> {
  initialData?: T;
  immediate?: boolean;
}

interface UseApiResult<T> {
  data: T | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useApi<T>(
  fetcher: () => Promise<{ success: boolean; data?: T; error?: string }>,
  options: UseApiOptions<T> = {}
): UseApiResult<T> {
  const { initialData = null, immediate = true } = options;
  
  const [data, setData] = useState<T | null>(initialData);
  const [isLoading, setIsLoading] = useState(immediate);
  const [error, setError] = useState<string | null>(null);

  const generation = useRef(0);
  const refetch = useCallback(async () => {
    const current = ++generation.current;
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await fetcher();
      if (current !== generation.current) return;
      if (response.success && response.data !== undefined) {
        setData(response.data);
      } else {
        setError(response.error || 'Failed to fetch data');
      }
    } catch (err) {
      if (current === generation.current) setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      if (current === generation.current) setIsLoading(false);
    }
  }, [fetcher]);

  const invalidate = useCallback(() => { generation.current++; }, []);
  useEffect(() => {
    if (immediate) {
      refetch();
    }
    return invalidate;
  }, [immediate, refetch, invalidate]);

  return { data, isLoading, error, refetch };
}

// Specific hooks for common data fetching
export function useModels(params?: { category?: string; featured?: boolean; page?: number }) {
  const { category, featured, page } = params || {};
  return useApi(useCallback(() => api.getModels({ category, featured, page }), [category, featured, page]));
}

export function useFeaturedModels() {
  return useApi(useCallback(() => api.getFeaturedModels(), []));
}

export function useModel(id: string) {
  return useApi(useCallback(() => api.getModel(id), [id]), { immediate: !!id });
}

export function useCategories() {
  return useApi(useCallback(() => api.getCategories(), []));
}

export function useRentals(status?: string) {
  return useApi(useCallback(() => api.getRentals(status), [status]));
}

export function useSubscriptionPlans() {
  return useApi(useCallback(() => api.getSubscriptionPlans(), []));
}

export function useUserSubscription() {
  return useApi(useCallback(() => api.getUserSubscription(), []));
}

export function useMarkets(params?: { category?: string; status?: string; featured?: boolean }) {
  const { category, status, featured } = params || {};
  return useApi(useCallback(() => api.getMarkets({ category, status, featured }), [category, status, featured]));
}

export function useMarket(id: string) {
  return useApi(useCallback(() => api.getMarket(id), [id]), { immediate: !!id });
}

export function usePositions() {
  return useApi(useCallback(() => api.getUserPositions(), []));
}

export function useWallet() {
  return useApi(useCallback(() => api.getWallet(), []));
}

export function useWalletTransactions(page?: number) {
  return useApi(useCallback(() => api.getWalletTransactions(page), [page]));
}

export default useApi;
